import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  IconButton,
  Pagination,
  Card,
  CardContent,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Badge,
  LinearProgress,
  useTheme,
  alpha,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  QrCode as QrCodeIcon,
  QrCode2 as QrCode2Icon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  RestoreFromTrash as RestoreIcon,
  AccountBalance as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccessTime as ClockIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import billService, { Bill, BillFilters, DashboardStats } from '../services/billService';
import spopService from '../services/spopService';
import CreateBillDialog from '../components/CreateBillDialog';
import ViewBillDialog from '../components/ViewBillDialog';
import EditBillDialog from '../components/EditBillDialog';
import PayBillDialog from '../components/PayBillDialog';
import PaymentDialog from '../components/PaymentDialog';
import PaybosPaymentDialog from '../components/PaybosPaymentDialog';
import tripayService from '../services/tripayService';
import GenerateBillsDialog from '../components/GenerateBillsDialog';
import { useAuth } from '../hooks/useAuth';

// Add styles for spin animation
const styles = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

// Financial Info Card Component
interface FinancialCardProps {
  title: string;
  amount: number;
  icon: React.ReactElement;
  color: string;
  trend?: number;
  description?: string;
}

const FinancialCard: React.FC<FinancialCardProps> = ({ 
  title, 
  amount, 
  icon, 
  color, 
  trend, 
  description 
}) => {
  const theme = useTheme();
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.25)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom 
              fontWeight="medium"
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="h3" 
              fontWeight="bold" 
              color="primary.main"
              sx={{ mb: 1 }}
            >
              {formatCurrency(amount)}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
            {trend !== undefined && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend >= 0 ? (
                  <TrendingUpIcon color="success" fontSize="small" />
                ) : (
                  <TrendingDownIcon color="error" fontSize="small" />
                )}
                <Typography
                  variant="body2"
                  color={trend >= 0 ? 'success.main' : 'error.main'}
                  ml={0.5}
                  fontWeight="medium"
                >
                  {Math.abs(trend)}% dari bulan lalu
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: color, 
              width: 56, 
              height: 56,
              boxShadow: `0 4px 14px ${alpha(color, 0.4)}`,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

const Bills: React.FC = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paybosPaymentDialogOpen, setPaybosPaymentDialogOpen] = useState(false);
  const [gateways, setGateways] = useState({
    tripay: { configured: false, enabled: false },
    paybos: { configured: false, enabled: false },
  });
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [selectedSpopFilter, setSelectedSpopFilter] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState<Record<string, boolean>>({});
  
  const { user } = useAuth();
  
  // Get SPOP filter for operators
  const operatorSpopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;
  
  // WhatsApp states
  const [whatsappLoading, setWhatsappLoading] = useState<Record<string, boolean>>({});
  const [bulkReminderDialog, setBulkReminderDialog] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<string[]>([]);
  const [whatsappMessage, setWhatsappMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  // Bill Jobs states
  const [billJobLoading, setBillJobLoading] = useState<Record<string, boolean>>({});
  const [billJobMessage, setBillJobMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [filters, setFilters] = useState<BillFilters>({
    search: '',
    status: '',
    month: '',
    year: '',
    page: 1,
    limit: 10,
    ...(operatorSpopFilter && { spop_id: operatorSpopFilter }),
  });

  const fetchBills = async () => {
    try {
      setLoading(true);
      
      // Gabungkan filter yang ada dengan filter SPOP
      const finalFilters = { ...filters };
      
      // Apply operator SPOP filter if user is operator
      if (operatorSpopFilter) {
        finalFilters.spop_id = operatorSpopFilter;
      }
      // Apply selected SPOP filter if user is admin and has selected a SPOP
      else if (selectedSpopFilter) {
        finalFilters.spop_id = selectedSpopFilter;
      }
      
      const response = await billService.getBills(finalFilters);
      
      // Backend sudah melakukan sorting yang optimal, tidak perlu sorting ulang di frontend
      setBills(response.data.bills);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bills');
    } finally {
      setLoading(false);
    }
  };

  // Get SPOPs for filter (only for admin users)
  const [spopsData, setSpopsData] = useState<any>(null);
  const [spopsLoading, setSpopsLoading] = useState(false);
  
  const fetchSpops = async () => {
    if (user?.role === 'admin') {
      try {
        setSpopsLoading(true);
        const response = await spopService.getAllSPOPs();
        setSpopsData(response.data);
      } catch (error) {
        console.error('Failed to fetch SPOPs:', error);
      } finally {
        setSpopsLoading(false);
      }
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const stats = await billService.getBillingStats();
      setDashboardStats(stats.data);
    } catch (err: any) {
      console.error('Failed to fetch billing stats:', err);
      setDashboardStats({
        monthly: {
          pending: { count: 0, total_amount: 0 },
          paid: { count: 0, total_amount: 0 },
          overdue: { count: 0, total_amount: 0 },
          cancelled: { count: 0, total_amount: 0 }
        },
        today_due: 0,
        today_due_amount: 0,
        total_overdue: 0
      });
    }
  };

  const fetchGatewaysStatus = async () => {
    try {
      const response = await tripayService.public.getGatewaysStatus();
      if (response.success) {
        setGateways(response.data);
      }
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchBills();
  }, [filters, selectedSpopFilter]);

  useEffect(() => {
    fetchDashboardStats();
    fetchSpops();
    fetchGatewaysStatus();
  }, [user]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setFilters(prev => ({ ...prev, page: 1 }));
  }, [selectedSpopFilter]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID');
  };

  const handleFilterChange = (field: keyof BillFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value, // Reset page when other filters change
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      month: '',
      year: '',
      page: 1,
      limit: 10,
      ...(operatorSpopFilter && { spop_id: operatorSpopFilter }),
    });
    setSelectedSpopFilter('');
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handleFilterChange('page', value);
  };

  const handleView = (bill: Bill) => {
    setSelectedBill(bill);
    setViewDialogOpen(true);
  };

  const handleEdit = (bill: Bill) => {
    setSelectedBill(bill);
    setEditDialogOpen(true);
  };

  const handlePay = (bill: Bill) => {
    if (bill.status === 'paid') {
      setError('Tagihan ini sudah dibayar');
      return;
    }
    setSelectedBill(bill);
    setPayDialogOpen(true);
  };

  const handleCreatePayment = (bill: Bill) => {
    if (bill.status === 'paid') {
      setError('Tagihan ini sudah dibayar');
      return;
    }
    setSelectedBill(bill);
    setPaymentDialogOpen(true);
  };

  const handleCreatePaybosPayment = (bill: Bill) => {
    if (bill.status === 'paid') {
      setError('Tagihan ini sudah dibayar');
      return;
    }
    setSelectedBill(bill);
    setPaybosPaymentDialogOpen(true);
  };

  const handleDelete = (bill: Bill) => {
    setSelectedBill(bill);
    setDeleteDialogOpen(true);
  };

  const handleCopyPaymentLink = (bill: Bill) => {
    const publicUrl = `${window.location.origin}/payment/bill/${bill.id}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setSuccess('Link pembayaran Tripay telah disalin ke clipboard');
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Gagal menyalin link');
    });
  };

  const handleCopyPaybosPaymentLink = (bill: Bill) => {
    const publicUrl = `${window.location.origin}/payment/paybos/bill/${bill.id}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setSuccess('Link pembayaran PayBOS telah disalin ke clipboard');
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Gagal menyalin link');
    });
  };

  const confirmDelete = async () => {
    if (!selectedBill) return;

    try {
      await billService.deleteBill(selectedBill.id);
      setDeleteDialogOpen(false);
      setSelectedBill(null);
      fetchBills();
      fetchDashboardStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete bill');
    }
  };

  const handleSuccess = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setPayDialogOpen(false);
    setGenerateDialogOpen(false);
    setSelectedBill(null);
    fetchBills();
    fetchDashboardStats();
  };

  // Sukses pembayaran cash: tampilkan juga hasil reaktivasi customer
  const handlePaySuccess = (result?: any) => {
    const reactivation = result?.data?.customer_reactivation;
    if (reactivation) {
      if (reactivation.reactivated) {
        const pppInfo = reactivation.ppp_enabled
          ? 'PPP diaktifkan'
          : `PPP GAGAL diaktifkan${reactivation.ppp_error ? ` (${reactivation.ppp_error})` : reactivation.ppp_skipped ? ` (${reactivation.ppp_skipped})` : ''} — cek MikroTik`;
        const olderInfo = reactivation.unpaid_older_bills
          ? ` Catatan: masih ada ${reactivation.unpaid_older_bills} tunggakan lama tercatat.`
          : '';
        setSuccess(`Pembayaran berhasil. Customer direaktivasi, ${pppInfo}.${olderInfo}`);
      } else {
        setSuccess(`Pembayaran berhasil. Customer TIDAK direaktivasi: ${reactivation.reason || reactivation.error || 'alasan tidak diketahui'}`);
      }
    } else {
      setSuccess('Pembayaran berhasil dicatat.');
    }
    handleSuccess();
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Menunggu';
      case 'paid':
        return 'Lunas';
      case 'overdue':
        return 'Terlambat';
      case 'cancelled':
        return 'Dibatalkan';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Export functionality
  const exportBills = async () => {
    setIsExporting(true);
    try {
      // Gabungkan filter yang sama dengan yang digunakan untuk fetch bills
      const exportFilters = { ...filters };
      
      // Apply operator SPOP filter if user is operator
      if (operatorSpopFilter) {
        exportFilters.spop_id = operatorSpopFilter;
      }
      // Apply selected SPOP filter if user is admin and has selected a SPOP
      else if (selectedSpopFilter) {
        exportFilters.spop_id = selectedSpopFilter;
      }

      console.log('Exporting bills with filters:', exportFilters); // Debug log
      
      await billService.exportBills(exportFilters);
      setSuccess('Data tagihan berhasil diekspor ke file Excel');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Export error:', error);
      setError('Terjadi kesalahan saat mengekspor data: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsExporting(false);
    }
  };

  const downloadInvoice = async (bill: Bill) => {
    setInvoiceLoading((prev) => ({ ...prev, [bill.id]: true }));
    try {
      await billService.downloadInvoice(bill.id, bill.bill_number);
      setSuccess(`Invoice ${bill.bill_number} berhasil diunduh`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Gagal mengunduh invoice PDF');
    } finally {
      setInvoiceLoading((prev) => ({ ...prev, [bill.id]: false }));
    }
  };

  // WhatsApp functions
  // Kirim reminder langsung ke WhatsApp aplikasi
  const buildPaymentLinkText = (billId: string) => {
    const base = window.location.origin;
    const tripay = `${base}/payment/bill/${billId}`;
    const paybos = `${base}/payment/paybos/bill/${billId}`;
    if (gateways.tripay.enabled && gateways.paybos.enabled) {
      return `🏦 Tripay (VA/Transfer Bank):\n${tripay}\n\n📱 PayBOS QRIS:\n${paybos}`;
    }
    if (gateways.tripay.enabled) return tripay;
    if (gateways.paybos.enabled) return paybos;
    return tripay;
  };

  const sendBillReminder = (bill: Bill) => {
    const phone = bill.customer?.phone;
    if (!phone) {
      setWhatsappMessage({ type: 'error', text: 'Nomor WhatsApp customer tidak tersedia' });
      return;
    }
    let waNumber = phone.replace(/[^0-9]/g, '');
    if (waNumber.startsWith('0')) waNumber = '62' + waNumber.slice(1);
    const pesan =
      `Halo ${bill.customer?.name},\n` +
      `Ini adalah pengingat tagihan Anda.\n` +
      `Nomor Tagihan: ${bill.bill_number}\n` +
      `Total: ${formatCurrency(bill.total_amount)}\n` +
      `Jatuh Tempo: ${formatDate(bill.due_date)}\n` +
      `Silakan lakukan pembayaran sebelum jatuh tempo.\n` +
      `Link pembayaran:\n${buildPaymentLinkText(bill.id)}`;
    const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(pesan)}`;
    window.open(waUrl, '_blank');
  };

  const sendOverdueNotice = async (billId: string) => {
    setWhatsappLoading(prev => ({ ...prev, [`overdue_${billId}`]: true }));
    
    try {
      const data = await billService.sendOverdueNotice(billId);
      
      if (data.success) {
        setWhatsappMessage({ type: 'success', text: 'Peringatan tunggakan berhasil dikirim via WhatsApp' });
      } else {
        setWhatsappMessage({ type: 'error', text: data.message });
      }
    } catch (error: any) {
      setWhatsappMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Gagal mengirim peringatan tunggakan' 
      });
    } finally {
      setWhatsappLoading(prev => ({ ...prev, [`overdue_${billId}`]: false }));
    }
  };

  const sendBulkReminders = async () => {
    if (selectedBillIds.length === 0) {
      setWhatsappMessage({ type: 'error', text: 'Pilih minimal satu tagihan' });
      return;
    }

    setWhatsappLoading(prev => ({ ...prev, bulk: true }));
    
    try {
      const data = await billService.sendBulkReminders(selectedBillIds);
      
      if (data.success) {
        setWhatsappMessage({ 
          type: 'success', 
          text: `${data.message} - Berhasil: ${data.data.success}, Gagal: ${data.data.failed}` 
        });
        setSelectedBillIds([]);
        setBulkReminderDialog(false);
      } else {
        setWhatsappMessage({ type: 'error', text: data.message });
      }
    } catch (error: any) {
      setWhatsappMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Gagal mengirim bulk reminder' 
      });
    } finally {
      setWhatsappLoading(prev => ({ ...prev, bulk: false }));
    }
  };

  const handleBillSelection = (billId: string, checked: boolean) => {
    if (checked) {
      setSelectedBillIds(prev => [...prev, billId]);
    } else {
      setSelectedBillIds(prev => prev.filter(id => id !== billId));
    }
  };

  const isOverdue = (bill: Bill) => {
    return bill.status === 'pending' && new Date(bill.due_date) < new Date();
  };

  // Bill Job functions
  const processOverdueBills = async () => {
    if (!window.confirm('Proses tagihan overdue sekarang? Customer dengan tagihan jatuh tempo akan disuspend dan PPP dinonaktifkan.')) {
      return;
    }

    setBillJobLoading(prev => ({ ...prev, overdue: true }));
    
    try {
      const data = await billService.processOverdueBills();
      
      if (data.success) {
        const overdueDetails = `${data.data.processedBills || 0} tagihan diproses (${data.data.alreadyOverdueBills || 0} sudah overdue), ${data.data.suspendedCustomers || 0} customer suspend (${data.data.alreadySuspendedCustomers || 0} sudah tersuspend), ${data.data.pppSecretsDisconnected || 0} PPP disabled & disconnected`;
        
        setBillJobMessage({ 
          type: 'success', 
          text: `✅ Berhasil memproses tagihan overdue: ${overdueDetails}` 
        });
        setTimeout(() => setBillJobMessage(null), 8000); // Increase timeout for longer message
        fetchBills();
        fetchDashboardStats();
      } else {
        setBillJobMessage({ type: 'error', text: data.message || 'Gagal memproses tagihan overdue' });
      }
    } catch (error: any) {
      setBillJobMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Terjadi kesalahan saat memproses tagihan overdue' 
      });
    } finally {
      setBillJobLoading(prev => ({ ...prev, overdue: false }));
    }
  };

  const processCustomerReactivation = async () => {
    if (!window.confirm('Jalankan reaktivasi customer sekarang? Semua customer suspended tanpa tagihan jatuh tempo akan diaktifkan kembali dan PPP diaktifkan.')) {
      return;
    }

    setBillJobLoading(prev => ({ ...prev, reactivation: true }));
    
    try {
      const data = await billService.processCustomerReactivation();
      
      if (data.success) {
        const errorsInfo = data.data.errorsCount ? `, ${data.data.errorsCount} error PPP (cek log)` : '';
        setBillJobMessage({ 
          type: 'success', 
          text: `✅ Reaktivasi selesai: ${data.data.reactivatedCustomers || 0} customer direaktivasi, ${data.data.pppSecretsEnabled || 0} PPP diaktifkan (${data.data.totalSuspendedCustomers || 0} suspended dicek)${errorsInfo}` 
        });
        setTimeout(() => setBillJobMessage(null), 8000);
        fetchBills();
        fetchDashboardStats();
      } else {
        setBillJobMessage({ type: 'error', text: data.message || 'Gagal memproses reaktivasi customer' });
      }
    } catch (error: any) {
      setBillJobMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Terjadi kesalahan saat memproses reaktivasi customer' 
      });
    } finally {
      setBillJobLoading(prev => ({ ...prev, reactivation: false }));
    }
  };

  const runAllBillJobs = async () => {
    if (!window.confirm('Jalankan semua job sekarang? Proses overdue (suspend) dan reaktivasi customer akan dijalankan berurutan.')) {
      return;
    }

    setBillJobLoading(prev => ({ ...prev, all: true }));
    
    try {
      const data = await billService.runAllBillJobs();
      
      if (data.success) {
        const overdueData = data.data?.overdue_processing?.summary;
        const reactivationData = data.data?.reactivation_processing?.summary;
        
        const overdueInfo = overdueData
          ? `${overdueData.bills_marked_overdue || 0} tagihan diproses (${overdueData.bills_already_overdue || 0} sudah overdue), ${overdueData.customers_suspended || 0} customer suspend (${overdueData.customers_already_suspended || 0} sudah tersuspend), ${overdueData.ppp_secrets_disabled_and_disconnected || 0} PPP disconnect`
          : 'proses overdue selesai';
        const reactivationInfo = reactivationData
          ? `${reactivationData.customers_reactivated || 0} customer direaktivasi`
          : 'proses reaktivasi selesai';
        
        setBillJobMessage({ 
          type: 'success', 
          text: `✅ Semua job berhasil dijalankan - Overdue: ${overdueInfo} | Reaktivasi: ${reactivationInfo}` 
        });
        setTimeout(() => setBillJobMessage(null), 10000); // Increase timeout for longer message
        fetchBills();
        fetchDashboardStats();
      } else {
        setBillJobMessage({ type: 'error', text: data.message || 'Gagal menjalankan semua job' });
      }
    } catch (error: any) {
      setBillJobMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Terjadi kesalahan saat menjalankan semua job' 
      });
    } finally {
      setBillJobLoading(prev => ({ ...prev, all: false }));
    }
  };

  if (loading && (!bills || bills.length === 0)) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary.main" gutterBottom>
              Manajemen Tagihan
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Kelola dan pantau semua tagihan pelanggan dengan mudah
            </Typography>
            {user?.role === 'operator' && user?.spop && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                SPOP: {user.spop.name} ({user.spop.areaCode})
              </Typography>
            )}
            {user?.role === 'admin' && selectedSpopFilter && spopsData?.data?.spops && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                Filter SPOP: {spopsData.data.spops.find((s: any) => s.id == selectedSpopFilter)?.name || 'Unknown'} 
                ({spopsData.data.spops.find((s: any) => s.id == selectedSpopFilter)?.areaCode || '-'})
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip 
              label="Real-time Data" 
              color="success" 
              size="small" 
              icon={<CheckIcon />}
            />
            <Typography variant="body2" color="text.secondary">
              Update: {new Date().toLocaleTimeString('id-ID')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
        
      {/* Financial Overview Cards */}
      {dashboardStats && dashboardStats.monthly && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Ringkasan Keuangan
          </Typography>
          <Grid container spacing={3}>
            {/* Total Pendapatan (Paid Bills) */}
            <Grid>
              <FinancialCard
                title="Total Pendapatan"
                amount={dashboardStats.monthly.paid?.total_amount || 0}
                icon={<MoneyIcon />}
                color="#4caf50"
                description={`${dashboardStats.monthly.paid?.count || 0} tagihan lunas`}
                trend={12}
              />
            </Grid>
            {/* Outstanding Bills (Pending) */}
            <Grid>
              <FinancialCard
                title="Tagihan Belum Bayar"
                amount={dashboardStats.monthly.pending?.total_amount || 0}
                icon={<ClockIcon />}
                color="#ff9800"
                description={`${dashboardStats.monthly.pending?.count || 0} tagihan pending`}
                trend={-5}
              />
            </Grid>
            {/* Overdue Bills */}
            <Grid >
              <FinancialCard
                title="Tagihan Terlambat"
                amount={dashboardStats.monthly.overdue?.total_amount || 0}
                icon={<WarningIcon />}
                color="#f44336"
                description={`${dashboardStats.monthly.overdue?.count || 0} tagihan overdue`}
                trend={8}
              />
            </Grid>
            {/* Today Due Bills */}
            <Grid>
              <FinancialCard
                title="Jatuh Tempo Hari Ini"
                amount={dashboardStats.today_due_amount || 0}
                icon={<CalendarIcon />}
                color="#2196f3"
                description={`${dashboardStats.today_due || 0} tagihan`}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Action Buttons Section */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
      >
        <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
          Aksi Cepat
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            Buat Tagihan Baru
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={() => setGenerateDialogOpen(true)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            Billing Otomatis
          </Button>
          
          {/* Manual Bill Job Buttons (endpoint hanya untuk admin) */}
          {user?.role === 'admin' && (
            <>
              <Button
                variant="outlined"
                size="large"
                color="warning"
                startIcon={billJobLoading.overdue ?
                  <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> :
                  <ScheduleIcon />
                }
                onClick={processOverdueBills}
                disabled={billJobLoading.overdue}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3
                }}
              >
                {billJobLoading.overdue ? 'Memproses...' : 'Proses Overdue'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                color="success"
                startIcon={billJobLoading.reactivation ?
                  <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> :
                  <RestoreIcon />
                }
                onClick={processCustomerReactivation}
                disabled={billJobLoading.reactivation}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3
                }}
              >
                {billJobLoading.reactivation ? 'Memproses...' : 'Reaktivasi Customer'}
              </Button>
              <Button
                variant="contained"
                size="large"
                color="secondary"
                startIcon={billJobLoading.all ?
                  <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> :
                  <PlayIcon />
                }
                onClick={runAllBillJobs}
                disabled={billJobLoading.all}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3
                }}
              >
                {billJobLoading.all ? 'Menjalankan...' : 'Jalankan Semua Job'}
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Filters Section */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          '& .MuiFormControl-root': {
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'white' },
            },
            '& .MuiSelect-icon': { color: 'white' },
          },
          '& .MuiTextField-root': {
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'white' },
            },
          }
        }}
      >
        <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Filter & Pencarian
        </Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid >
            <TextField
              fullWidth
              label="Pencarian"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Nomor tagihan, nama customer..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.8)' }} />,
              }}
            />
          </Grid>
          <Grid>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="pending">Menunggu</MenuItem>
                <MenuItem value="paid">Lunas</MenuItem>
                <MenuItem value="overdue">Terlambat</MenuItem>
                <MenuItem value="cancelled">Dibatalkan</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid >
            <FormControl fullWidth>
              <InputLabel>Bulan Jatuh Tempo</InputLabel>
              <Select
                value={filters.month}
                label="Bulan Jatuh Tempo"
                onChange={(e) => handleFilterChange('month', e.target.value)}
              >
                <MenuItem value="">Semua</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('id-ID', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <TextField
              fullWidth
              label="Tahun Jatuh Tempo"
              type="number"
              value={filters.year}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              placeholder={new Date().getFullYear().toString()}
            />
          </Grid>
          {user?.role === 'admin' && (
            <Grid>
              <FormControl fullWidth>
                <InputLabel>SPOP</InputLabel>
                <Select
                  value={selectedSpopFilter}
                  label="SPOP"
                  onChange={(e) => setSelectedSpopFilter(e.target.value)}
                  disabled={spopsLoading}
                >
                  <MenuItem value="">Semua SPOP</MenuItem>
                  {spopsLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Memuat SPOP...
                    </MenuItem>
                  ) : (
                    spopsData?.data?.spops && Array.isArray(spopsData.data.spops) && 
                    spopsData.data.spops.map((spop: any) => (
                      <MenuItem key={spop.id} value={spop.id}>
                        {spop.name} ({spop.areaCode})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid >
            <FormControl fullWidth>
              <InputLabel>Per Halaman</InputLabel>
              <Select
                value={filters.limit}
                label="Per Halaman"
                onChange={(e) => handleFilterChange('limit', e.target.value)}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={handleClearFilters}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                height: '56px',
                mr: 1
              }}
            >
              Reset Filter
            </Button>
          </Grid>
          <Grid >
            <Button
              variant="outlined"
              fullWidth
              startIcon={isExporting ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <DownloadIcon />}
              onClick={exportBills}
              disabled={isExporting}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                height: '56px'
              }}
            >
              {isExporting ? 'Mengekspor...' : 'Export Excel'}
            </Button>
          </Grid>
        </Grid>
        
        {/* Filter Summary */}
        {(filters.search || filters.status || filters.month || filters.year || selectedSpopFilter) && (
          <Box mt={2} p={2} bgcolor="rgba(255,255,255,0.1)" borderRadius={1}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
              Filter aktif:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {filters.search && (
                <Chip 
                  label={`Pencarian: "${filters.search}"`} 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
              {filters.status && (
                <Chip 
                  label={`Status: ${filters.status}`} 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
              {filters.month && (
                <Chip 
                  label={`Jatuh Tempo: ${new Date(0, parseInt(filters.month) - 1).toLocaleString('id-ID', { month: 'long' })} ${filters.year || new Date().getFullYear()}`} 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
              {filters.year && !filters.month && (
                <Chip 
                  label={`Tahun Jatuh Tempo: ${filters.year}`} 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
              {selectedSpopFilter && spopsData?.data?.spops && (
                <Chip 
                  label={`SPOP: ${spopsData.data.spops.find((s: any) => s.id == selectedSpopFilter)?.name || selectedSpopFilter}`} 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }} 
                />
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* WhatsApp Notification Alert */}
      {whatsappMessage && (
        <Alert 
          severity={whatsappMessage.type} 
          sx={{ mb: 2 }}
          onClose={() => setWhatsappMessage(null)}
        >
          {whatsappMessage.text}
        </Alert>
      )}

      {/* Bill Job Notification Alert */}
      {billJobMessage && (
        <Alert 
          severity={billJobMessage.type} 
          sx={{ mb: 2 }}
          onClose={() => setBillJobMessage(null)}
        >
          {billJobMessage.text}
        </Alert>
      )}

      {/* Bulk Actions */}
      {selectedBillIds.length > 0 && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Badge badgeContent={selectedBillIds.length} color="error">
              <Typography variant="h6" fontWeight="bold">
                Aksi Massal
              </Typography>
            </Badge>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {selectedBillIds.length} tagihan dipilih
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={() => setBulkReminderDialog(true)}
              disabled={whatsappLoading.bulk}
              sx={{ 
                textTransform: 'none',
                fontWeight: 'bold' 
              }}
            >
              Kirim Reminder WhatsApp
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedBillIds([])}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                textTransform: 'none',
                fontWeight: 'bold'
              }}
            >
              Batal Pilih
            </Button>
          </Box>
        </Paper>
      )}

      {/* Bills Table */}
      <Paper 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        <Box 
          sx={{ 
            p: 3, 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h6" component="h3" fontWeight="bold">
            Daftar Tagihan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total: {pagination.total} tagihan
          </Typography>
        </Box>
        <TableContainer>
          <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(0,0,0,0.05)' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                <TableCell 
                  padding="checkbox"
                  sx={{ fontWeight: 'bold' }}
                >
                  <input 
                    type="checkbox" 
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedBillIds(bills.filter(b => b.status === 'pending').map(b => b.id));
                      } else {
                        setSelectedBillIds([]);
                      }
                    }}
                    checked={selectedBillIds.length > 0 && selectedBillIds.length === bills.filter(b => b.status === 'pending').length}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Nomor Tagihan</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Paket</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Periode</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Jatuh Tempo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bills && bills.length > 0 ? bills.map((bill, index) => (
                <TableRow 
                  key={bill.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      transform: 'scale(1.001)',
                      transition: 'all 0.2s ease'
                    },
                    backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.01)'
                  }}
                >
                  <TableCell padding="checkbox">
                    <input 
                      type="checkbox" 
                      checked={selectedBillIds.includes(bill.id)}
                      onChange={(e) => handleBillSelection(bill.id, e.target.checked)}
                      disabled={bill.status === 'paid'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {bill.bill_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {bill.customer?.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {bill.customer?.customer_code}
                      </Typography>
                      {bill.customer?.phone && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {bill.customer.phone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={bill.package?.name} 
                      size="small" 
                      color="info"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(bill.billing_period_start)} - {formatDate(bill.billing_period_end)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={isOverdue(bill) ? 'error.main' : 'text.primary'}
                      fontWeight={isOverdue(bill) ? 'bold' : 'normal'}
                    >
                      {formatDate(bill.due_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(bill.total_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(bill.status)} 
                      color={getStatusColor(bill.status)}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Tooltip title="Lihat Detail">
                        <IconButton
                          size="small"
                          onClick={() => handleView(bill)}
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Unduh Invoice PDF">
                        <IconButton
                          size="small"
                          onClick={() => downloadInvoice(bill)}
                          disabled={invoiceLoading[bill.id]}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'error.light', color: 'white' },
                          }}
                        >
                          {invoiceLoading[bill.id] ? (
                            <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <PdfIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Tagihan">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(bill)}
                          sx={{ 
                            color: 'info.main',
                            '&:hover': { backgroundColor: 'info.light', color: 'white' }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {bill.status !== 'paid' && (
                        <>
                          <Tooltip title="Bayar Tagihan">
                            <IconButton
                              size="small"
                              onClick={() => handlePay(bill)}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { backgroundColor: 'success.light', color: 'white' }
                              }}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Tooltip>
                          {gateways.tripay.configured && gateways.tripay.enabled && (
                            <>
                              <Tooltip title="Buat Link Pembayaran (Tripay)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCreatePayment(bill)}
                                  sx={{
                                    color: 'secondary.main',
                                    '&:hover': { backgroundColor: 'secondary.light', color: 'white' },
                                  }}
                                >
                                  <QrCodeIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Link Pembayaran Public (Tripay)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyPaymentLink(bill)}
                                  sx={{
                                    color: 'info.main',
                                    '&:hover': { backgroundColor: 'info.light', color: 'white' },
                                  }}
                                >
                                  <LinkIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {gateways.paybos.configured && gateways.paybos.enabled && (
                            <>
                              <Tooltip title="Buat Pembayaran QRIS (PayBOS)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCreatePaybosPayment(bill)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                                  }}
                                >
                                  <QrCode2Icon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Copy Link PayBOS">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyPaybosPaymentLink(bill)}
                                  sx={{
                                    color: 'primary.dark',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                                  }}
                                >
                                  <LinkIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Kirim Reminder WhatsApp">
                            <IconButton
                              size="small"
                              onClick={() => sendBillReminder(bill)}
                              sx={{ 
                                color: 'success.main',
                                '&:hover': { backgroundColor: 'success.light', color: 'white' }
                              }}
                            >
                              <WhatsAppIcon />
                            </IconButton>
                          </Tooltip>
                          {isOverdue(bill) && (
                            <Tooltip title="Kirim Peringatan Tunggakan">
                              <IconButton
                                size="small"
                                onClick={() => sendOverdueNotice(bill.id)}
                                disabled={whatsappLoading[`overdue_${bill.id}`]}
                                sx={{ 
                                  color: 'warning.main',
                                  '&:hover': { backgroundColor: 'warning.light', color: 'white' }
                                }}
                              >
                                {whatsappLoading[`overdue_${bill.id}`] ? (
                                  <SendIcon sx={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                  <WarningIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      <Tooltip title="Hapus Tagihan">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(bill)}
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'error.light', color: 'white' }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ReportIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        {loading ? 'Memuat data...' : 'Tidak ada data tagihan'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {loading ? 'Mohon tunggu sebentar' : 'Silakan buat tagihan baru atau ubah filter pencarian'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 3,
              borderTop: '1px solid rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Halaman {pagination.currentPage} dari {pagination.totalPages} 
              ({pagination.total} total tagihan)
            </Typography>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 'bold',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    }
                  }
                }
              }}
            />
          </Box>
        )}
        
        {/* Loading Progress Bar */}
        {loading && (
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <LinearProgress />
          </Box>
        )}
      </Paper>

      {/* Create Bill Dialog */}
      <CreateBillDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleSuccess}
      />

      {/* View Bill Dialog */}
      {selectedBill && (
        <ViewBillDialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          bill={selectedBill}
        />
      )}

      {/* Edit Bill Dialog */}
      {selectedBill && (
        <EditBillDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleSuccess}
          bill={selectedBill}
        />
      )}

      {/* Pay Bill Dialog */}
      {selectedBill && (
        <PayBillDialog
          open={payDialogOpen}
          onClose={() => setPayDialogOpen(false)}
          onSuccess={handlePaySuccess}
          bill={selectedBill}
        />
      )}

      {/* Generate Bills Dialog */}
      <GenerateBillsDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Konfirmasi Hapus</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin menghapus tagihan {selectedBill?.bill_number}?
            Tindakan ini tidak dapat dibatalkan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Batal</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Hapus
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reminder Dialog */}
      <Dialog open={bulkReminderDialog} onClose={() => setBulkReminderDialog(false)}>
        <DialogTitle>Kirim Reminder WhatsApp</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Apakah Anda yakin ingin mengirim reminder WhatsApp untuk {selectedBillIds.length} tagihan yang dipilih?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Reminder akan dikirim ke nomor WhatsApp customer atau grup yang telah dikonfigurasi.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReminderDialog(false)}>Batal</Button>
          <Button 
            onClick={sendBulkReminders} 
            variant="contained" 
            startIcon={<WhatsAppIcon />}
            disabled={whatsappLoading.bulk}
          >
            {whatsappLoading.bulk ? 'Mengirim...' : 'Kirim Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedBill && (
        <PaymentDialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setSelectedBill(null);
          }}
          billId={selectedBill.id}
          billNumber={selectedBill.bill_number}
          totalAmount={selectedBill.total_amount}
          customerName={selectedBill.customer?.name}
          customerEmail={selectedBill.customer?.email}
          customerPhone={selectedBill.customer?.phone}
          onPaymentCreated={() => fetchBills()}
        />
      )}

      {selectedBill && (
        <PaybosPaymentDialog
          open={paybosPaymentDialogOpen}
          onClose={() => {
            setPaybosPaymentDialogOpen(false);
            setSelectedBill(null);
          }}
          billId={selectedBill.id}
          billNumber={selectedBill.bill_number}
          totalAmount={selectedBill.total_amount}
          customerName={selectedBill.customer?.name}
          customerEmail={selectedBill.customer?.email}
          customerPhone={selectedBill.customer?.phone}
          onPaymentCreated={() => fetchBills()}
        />
      )}
    </Container>
  );
};

export default Bills;
